import { useState, useRef, useLayoutEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import axios from "axios";
import {
  increaseQty,
  decreaseQty,
  setDiscount,
  setOrderType,
  setPaymentType,
  setCustomerId,
  setTableNumber,
  setNote,
} from "./orderSlice";
import Select from "react-select";
import { useEffect, useMemo } from "react";
import {
  setCustomers,
  setCustomerDataFetched,
} from "../customer/customerSlice";
import { Typography } from "@material-ui/core";
import { constants } from "../../Helpers/constantsFile";
import { clearCart } from "./orderSlice";
import moment from "moment";
// 🔹 ADDED
import PrinterOrder from "./PrintOrder";
import { addOrder, setOrderListDataFetched } from "./orderListSlice";
import { printedNotice } from "../../SignupAndLogin/loginSlice";
import { useNavigate, useLocation } from "react-router-dom";
import { normalisePlan } from "../printing/printPlan";
import { describeFailures, printStationTickets } from "../printing/printJobs";
import { RECEIPT_PRINTER, printReceipt } from "../printing/receipt";
import { humaniseError } from "../printing/qzPrinting";
import CartStatusBar from "./CartStatusBar";
import RecentOrders from "./RecentOrders";
import RecentOrderDialog from "./RecentOrderDialog";
import { fetchMyOrders, receiptDataFromOrder } from "./recentOrdersApi";
import { loadOrderToCart } from "./orderSlice";
import {
  CART_DROP_ID,
  useCartPulse,
  useCountUp,
  useOrderAnimations,
} from "./orders.animations";

const CartPanel = () => {
  const dispatch = useDispatch();
const navigate = useNavigate();
  const { cart, discount, orderType, editingOrderId, paymentType, customerId, tableNumber, note } =
  useSelector(state => state.order);

  const { products } = useSelector(state => state.products);
  const { customers, isCustomerDataFetched } = useSelector(
    state => state.customers
  );

  const { business, user } = useSelector(state => state.login.activeUser);
  const token = useSelector(state => state?.login?.token);
  const activeUser = useSelector(state => state.login.activeUser);

  // A waiter is signed in for exactly one order. Their session ends when the
  // receipt is done — see closePrint below.
  const isWaiter = activeUser?.role === "waiter";

  /**
   * Some shops want the receipt out of the printer the moment the order is
   * rung in; others want a cashier to look at it first. The business decides,
   * and the flag lives on the business so every till in the shop agrees.
   */
  const autoPrintReceipt = business?.autoPrintReceipt === true;

  /**
   * What a waiter is not asked.
   *
   * A waiter takes food to a table; the money is settled at the counter by
   * whoever is standing there. So the discount, how it is paid, the table and
   * the kitchen note are all left off their panel — the counter sets the first
   * two when it settles, and asking a waiter for a table number they are
   * standing at only slows the order down.
   *
   * The fields are not merely hidden: whatever is in the slice is ignored when
   * the payload is built, so a value left behind by an earlier session can
   * never ride along on a waiter's order.
   */
  const showBilling = !isWaiter;

  // The customer endpoints are restricted to manager / cashier / accountant,
  // so a waiter cannot read the list and therefore cannot bill one. Invoice is
  // not offered to them at all rather than offered and then failing.
  const canInvoice = !isWaiter;
  const isInvoice = paymentType === "invoice";
  const selectedCustomer = customers.find(c => c._id === customerId) || null;

    // 🔹 ADDED
  // The order is a network round trip and then, on an auto-print till, three
  // or four print jobs. Without this the cashier presses again.
  const [placing, setPlacing] = useState(false);
  const [printOpen, setPrintOpen] = useState(false);
  const [printData, setPrintData] = useState(null);
  const [placedOrderNumber, setPlacedOrderNumber] = useState(null);

  /**
   * A waiter's own orders from today, for the strip above the order type.
   *
   * Only fetched for a waiter: everyone else has the Orders screen, which does
   * this properly and for the whole shop.
   */
  const [myOrders, setMyOrders] = useState([]);
  const [myOrdersLoading, setMyOrdersLoading] = useState(false);
  const [openOrder, setOpenOrder] = useState(null);

  /**
   * A reprint is NOT the same event as the print that ends a shift.
   *
   * The dialog below closes with `closePrint`, which signs a waiter out — that
   * is the rule for the receipt of the order they just rang in. A waiter
   * looking at an older order must not be thrown back to the login screen for
   * it, so a reprint gets its own dialog whose close does nothing else.
   */
  const [reprintData, setReprintData] = useState(null);

  /**
   * Customers are fetched the first time somebody actually chooses Invoice,
   * not on every visit to the POS — most orders are cash and the list can be
   * long. The Customers tab fills the same slice, so whichever comes first
   * pays for it once.
   */
  useEffect(() => {
    if (!isInvoice || !canInvoice || isCustomerDataFetched) return;

    axios
      .get(
        `${constants.baseUrl}/customers/get-business-customers/${business?._id}`,
        { headers: { authorization: token } }
      )
      .then(res => {
        dispatch(setCustomers(res.data?.data?.customers || []));
        dispatch(setCustomerDataFetched(true));
      })
      .catch(err => {
        console.error("CUSTOMER LOAD ERROR:", err.response?.data || err.message);
      });
  }, [isInvoice, canInvoice, isCustomerDataFetched, business, token, dispatch]);

  /**
   * An order item never stored a picture, so a cart loaded from an existing
   * order arrives with none. Resolving it from the live product list here —
   * rather than at the moment the cart was filled — means an edited order
   * shows the same images as a fresh one.
   */
  const imageFor = item =>
    item.imageUrl ||
    products.find(p => (p.id || p._id) === item.productId)?.imageUrl ||
    "";

  const initialsFor = name => {
    const words = String(name || "").trim().split(/\s+/).filter(Boolean);
    if (!words.length) return "?";
    if (words.length === 1) return words[0].substring(0, 2).toUpperCase();
    return (words[0][0] + words[1][0]).toUpperCase();
  };

  const subtotal = cart.reduce((sum, i) => sum + i.price * i.qty, 0);

  /* ── motion ───────────────────────────────────────────────────────────────
     None of this decides anything. `pulse` says which line just changed so it
     can be pointed at, and the pill is a black rectangle that slides between
     the three order types instead of the colour jumping from one to the next.
     Every figure below still comes from `total`, never from what is shown. */

  useOrderAnimations();
  const pulse = useCartPulse(cart);

  const typesRef = useRef(null);
  const [pill, setPill] = useState(null);

  // Measured rather than guessed: "Takeaway" and "Dine-in" are not the same
  // width, and the pill has to land exactly where the black used to be.
  useLayoutEffect(() => {
    const box = typesRef.current;
    if (!box) return undefined;

    const measure = () => {
      const active = box.querySelector('[data-order-type="active"]');
      if (!active) {
        setPill(null);
        return;
      }
      setPill({
        left: active.offsetLeft,
        width: active.offsetWidth,
        height: active.offsetHeight,
      });
    };

    measure();
    window.addEventListener("resize", measure);
    return () => window.removeEventListener("resize", measure);
  }, [orderType, isWaiter, cart.length]);

  /** The button goes green for a moment once the order is away. */
  const [placed, setPlaced] = useState(false);

  // A waiter is signed out seconds after an order is placed, so the timer
  // below can easily outlive this panel. It checks before it speaks.
  const aliveRef = useRef(true);
  useEffect(() => {
    return () => {
      aliveRef.current = false;
    };
  }, []);

  // The one figure the whole screen agrees on — the total shown, the total
  // sent, and the total on the receipt all come from it. A waiter gives none.
  const appliedDiscount = showBilling ? Number(discount) || 0 : 0;
  const total = subtotal - appliedDiscount;
  const today = moment().format("YYYY-MM-DD hh:mm A");

  // Shown only. It rolls up to `total` over a third of a second so a change
  // registers out of the corner of the eye; what is sent is always `total`.
  const shownTotal = useCountUp(total);

  useEffect(() => {
    if (!isWaiter || !business?._id || !token) return undefined;

    let cancelled = false;
    setMyOrdersLoading(true);

    fetchMyOrders({ businessId: business._id, token })
      .then((rows) => {
        if (cancelled) return;
        setMyOrders(rows);
      })
      // Silent: the strip is a convenience, and a waiter with a table waiting
      // should not be shown an error about a list they did not ask for.
      .catch(() => {})
      .finally(() => {
        if (!cancelled) setMyOrdersLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [isWaiter, business, token]);

  /**
   * The order number behind `editingOrderId`, for the bar to name.
   *
   * Only the strip knows it — the slice stores an id and nothing else. A
   * manager who arrived here from the Orders screen has no strip, so the bar
   * says "Editing an order" rather than inventing a number.
   */
  const editingOrderNumber = useMemo(() => {
    if (!editingOrderId) return null;
    const match = myOrders.find((row) => row._id === editingOrderId);
    return match ? match.orderNumber : null;
  }, [myOrders, editingOrderId]);

  /** Load one of those orders onto the pad, replacing whatever is there. */
  const editRecentOrder = (order) => {
    setOpenOrder(null);
    dispatch(clearCart());
    dispatch(loadOrderToCart(order));
  };

  /**
   * Send the kitchen its tickets, and say what went wrong rather than saying
   * it out loud.
   *
   * An alert blocks the browser's only thread, so one raised here would stop
   * the receipt printing until somebody dismissed it. The caller decides when
   * to speak — after all the paper is out.
   *
   * Nothing here can fail the order: it was saved before any of this ran.
   *
   * @returns {Promise<string>} a sentence for the cashier, or '' if all well
   */
  const sendKitchenTickets = async ({ order, plan }) => {
    if (!plan.length) return "";

    try {
      const { failed } = await printStationTickets({
        order,
        plan,
        waiterName: user?.name || activeUser?.name || "",
      });
      return failed.length ? describeFailures(failed) : "";
    } catch (error) {
      return "The kitchen tickets did not print. Write them by hand.";
    }
  };

  // ✅ PLACE ORDER HANDLER
 const placeOrder = async () => {
  if (placing) return;
  if (!cart.length) return alert("Cart is empty!");

  // An invoice with no customer is what the API silently drops on the floor:
  // the order saves, but no transaction is written and nobody's book moves.
  if (isInvoice && !customerId) return alert("Choose the customer to bill");

  const payload = {
    items: cart.map(item => ({
      menuItem: item.productId,
      name: item.name,
      quantity: item.qty,
      price: item.price,
      subtotal: item.qty * item.price,
    })),
    discount: appliedDiscount,
    orderType,
    // Neither appears on the customer's receipt; both are printed large on
    // the kitchen ticket. A waiter is not asked for either, so a waiter's
    // order carries neither.
    tableNumber: showBilling ? tableNumber.trim() : "",
    note: showBilling ? note.trim() : "",
    date: today,
    // Cash for a waiter, always: they are not shown the choice, and an
    // invoice they cannot pick must never reach the server.
    paymentType: showBilling ? paymentType : "cash",
    // Only sent for an invoice — the server reads it to open the transaction
    // that moves this customer's balance.
    customer: isInvoice ? customerId : undefined,
    business: business?._id,
    user: user?._id,
    grossTotal: subtotal,
    total,
  };

  setPlacing(true);

  try {
    let res;
    if (editingOrderId) {
      // ✅ UPDATE existing order
      res = await axios.patch(
        `${constants.baseUrl}/orders/${editingOrderId}`,
        payload,
        { headers: { Authorization: token } }
      );
      // The Orders screen is not a place a waiter can go — the layout sends
      // them straight back — so only the counter is taken there.
      if (!isWaiter) navigate("/orderlist");
    } else {
      // ✅ CREATE new order
      res = await axios.post(`${constants.baseUrl}/orders`, payload, {
        headers: { Authorization: token },
      });
    }

    // 🔹 KITCHEN TICKETS
    //
    // Fired here, not awaited: the order is already saved, the cashier is
    // about to be shown the receipt, and the kitchen should not wait for a
    // modal. A printer problem surfaces on its own a moment later and can
    // never make a saved order look failed.
    const savedOrder = editingOrderId
      ? res.data?.data?.updatedOrder
      : res.data?.data?.createdOrder;

    // Held, not awaited here: the print dialog should appear at once. The
    // auto-print path below waits on it, so the kitchen's paper is always out
    // before the customer's.
    const ticketsSent = sendKitchenTickets({
      order: {
        orderNumber: savedOrder?.orderNumber ?? "",
        tableNumber: payload.tableNumber,
        orderType: payload.orderType,
        note: payload.note,
        // The order's own date, exactly as the server will store it. The
        // formatter reads it back as UTC, which is what turns the naive local
        // string the till writes into the time that was actually meant —
        // `new Date()` here would print three hours out.
        date: payload.date,
        isUpdate: Boolean(editingOrderId),
      },
      // The server builds the plan, on create and on edit alike, from the menu
      // it has just validated.
      //
      // On an EDIT that plan holds ONLY what the order gained — the server
      // diffs the saved items against the new ones before it overwrites them.
      // Rebuilding it here from the whole cart, as this used to, sent the
      // kitchen the entire order a second time and cooked every plate twice.
      // An edit that only moved a table number or a discount adds nothing, so
      // the plan comes back empty and no station hears about it at all.
      plan: normalisePlan(res.data?.data?.printPlan),
    });

    // 🔹 THE RECEIPT
    //
    // Built now, while the cart still has something in it. Whether it is
    // shown first or simply printed is the business's own setting.
    const receiptData = {
      discount: appliedDiscount,
      date: today,
      orderNumber: savedOrder?.orderNumber,
      // Off the order the server just saved. VAT is the business's setting
      // applied server-side, so the till cannot know the figures until the
      // order comes back — and must never work them out for itself.
      vatRate: savedOrder?.vatRate,
      vatAmount: savedOrder?.vatAmount,
      totalWithVat: savedOrder?.totalWithVat,
      // Printed where the receipt used to say TIXRAAC. Whoever is signed in is
      // whoever served this order — the same name the kitchen ticket carries.
      waiterName: user?.name || activeUser?.name || "",
      products: cart.map(item => ({
        name: item.name,
        qty: item.qty,
        salePrice: item.price,
      })),
    };

    if (!autoPrintReceipt) {
      setPrintData(receiptData);
      setPrintOpen(true);
      // The dialog is up; a ticket problem is reported whenever it lands.
      ticketsSent.then(problem => {
        if (problem) alert(problem);
      });
    }

    // ✅ ADD OR UPDATE ORDER LIST
    if (editingOrderId) {
      dispatch(setOrderListDataFetched(false)); // refresh order list
    } else {
      const createdOrder = res.data?.data?.createdOrder;
      dispatch(addOrder(createdOrder));
      setPlacedOrderNumber(createdOrder?.orderNumber ?? null);
    }

    // An invoice just moved this customer's balance server-side. Marking the
    // book unfetched is cheaper than guessing the new figure, and the next
    // screen that needs it pulls a correct one.
    if (isInvoice) dispatch(setCustomerDataFetched(false));

    dispatch(clearCart());

    // Decoration, and nothing else: the order is already saved, the tickets
    // are already on their way. The button turns green, says so, and turns
    // back — and if this screen is gone by then, nothing happens at all.
    setPlaced(true);
    window.setTimeout(() => {
      if (aliveRef.current) setPlaced(false);
    }, 1400);

    /**
     * THE BUTTON IS FREE FROM HERE.
     *
     * The order reached the server several lines above; everything below is
     * paper. It used to be inside the wait, so a till whose printer was off
     * sat on "Placing…" for as long as it took the printing layer to give up
     * — a saved order looking unsaved, which is how the same order gets rung
     * in twice on a busy night.
     *
     * Pressing again cannot double it: the cart was just emptied, and an
     * empty cart is refused at the top of this function.
     */
    if (aliveRef.current) setPlacing(false);

    // Auto-print goes last, after the order list is up to date, because a
    // waiter is signed out the moment the paper is out — the same rule the
    // dialog follows when it closes.
    if (autoPrintReceipt) {
      // The kitchen's paper goes first and the customer's last, so a runner
      // never picks up a receipt for food nobody has started.
      const ticketProblem = await ticketsSent;

      let receiptProblem = "";
      try {
        await printReceipt({ business, data: receiptData });
      } catch (error) {
        receiptProblem =
          "Could not print the receipt.\n\n" +
          humaniseError(error, { name: RECEIPT_PRINTER });
      }

      // Everything that was going to print has printed; now it is safe to
      // block on a dialog.
      const problems = [ticketProblem, receiptProblem].filter(Boolean);
      if (problems.length) alert(problems.join("\n\n"));

      finishAfterReceipt(savedOrder?.orderNumber ?? null);
    }
  } catch (err) {
    console.error("ORDER ERROR:", err.response?.data || err.message);
    alert("Failed to place order");
  } finally {
    // Usually already false by now — see the note above. It still runs, for
    // the path where the order itself failed and nothing was cleared.
    if (aliveRef.current) setPlacing(false);
  }
};

  /**
   * The print modal has closed — either the receipt printed, or whoever was
   * standing there cancelled the dialog.
   *
   * A waiter is signed out EITHER WAY. The order reached the server before
   * anything was sent to the printer, so nothing is lost by ending the
   * session here; a cancelled receipt is reprinted from the Orders screen by
   * someone who can open it.
   *
   * Sign-out waits for this rather than firing on the button press, so the
   * waiter watches their own order come out before the screen hands itself to
   * the next person.
   */
  const closePrint = () => {
    setPrintOpen(false);
    finishAfterReceipt(placedOrderNumber);
  };

  /**
   * The receipt is done — printed, or the dialog was dismissed.
   *
   * A waiter is signed out either way. The order reached the server long
   * before anything was sent to the printer, so nothing is lost by ending the
   * session here; a receipt nobody took is reprinted from the Orders screen by
   * someone who can open it.
   */
  const finishAfterReceipt = (orderNumber) => {
    if (!isWaiter) return;

    dispatch(clearCart());
    dispatch(printedNotice(orderNumber, activeUser?.name));
  };


  return (
    <>
    <div
      style={{
        display: "flex",
        flexDirection: "column",
        gap: "20px",
        height: "100%",
      }}
    >
      {/* WHAT THE PAD IS DOING, and the way out of it. Hides itself when the
          cart is empty and nothing is being edited. */}
      <CartStatusBar
        itemCount={cart.length}
        editing={Boolean(editingOrderId)}
        editingOrderNumber={editingOrderNumber}
        onClear={() => dispatch(clearCart())}
      />

      {/* CART ITEMS
          The id is where a tapped picture flies to — see orders.animations. */}
      <div id={CART_DROP_ID} style={{ maxHeight: "500px", overflowY: "auto" }}>
        {cart.map(item => (
          <div
            key={item.productId}
            /* A line that has just arrived slides in from the right; one that
               was already there and went up simply glows once. Either way the
               eye is told where to look without anything being moved. */
            className={
              "dt-line" +
              (pulse && pulse.id === item.productId
                ? pulse.fresh
                  ? " dt-line-enter"
                  : " dt-line-flash"
                : "")
            }
            style={{
              display: "flex",
              marginBottom: "15px",
              width: "100%",
            }}
          >
            {/* A fixed square. The old rule set only a width, so a portrait
                photo came back tall and pushed its row out of line with the
                others — which is what made the panel look ragged. */}
            {imageFor(item) ? (
              <img
                src={imageFor(item)}
                alt=""
                style={{
                  width: "50px",
                  height: "50px",
                  flexShrink: 0,
                  objectFit: "cover",
                  border: "3px solid #DEDEDE",
                  borderRadius: "5px",
                  boxSizing: "border-box",
                }}
              />
            ) : (
              <div
                style={{
                  width: "50px",
                  height: "50px",
                  flexShrink: 0,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  background: "#EBEBEB",
                  border: "3px solid #DEDEDE",
                  borderRadius: "5px",
                  boxSizing: "border-box",
                  fontSize: "16px",
                  fontWeight: "bold",
                  color: "#A9A9A9",
                }}
              >
                {initialsFor(item.name)}
              </div>
            )}

            <div style={{ flex: 1, marginLeft: "15px" }}>
              <Typography style={{ fontSize: "15px", fontWeight: "bold" }}>
                {item.name}
              </Typography>
              <Typography style={{ fontSize: "15px", color: "#595959" }}>
                {/* A unit price divided out of a pack — 1/7 of a dollar —
                    arrives as 0.1428571429. Money is shown to two places;
                    the value sent to the server stays exact. */}
                ${Number(item.price).toFixed(2)} × {item.qty} = $
                {(item.price * item.qty).toFixed(2)}
              </Typography>
            </div>

            <div
              style={{
                display: "flex",
                alignItems: "center",
                gap: "10px",
              }}
            >
              <div
                onClick={() => dispatch(decreaseQty(item.productId))}
                className="dt-step"
                style={{
                  background: "#D9D9D9",
                  borderRadius: "5px",
                  width: "30px",
                  height: "30px",
                  display: "flex",
                  justifyContent: "center",
                  alignItems: "center",
                  cursor: "pointer",
                }}
              >
                <Typography style={{ fontSize: "28px", userSelect: "none" }}>-</Typography>
              </div>
              <div style={{ width: "15px", display: "flex", justifyContent: "center", alignItems: "center" }}>
                {/* The number flicks up as it changes. Keyed on the quantity
                    so React hands it a fresh node and the flick replays. */}
                <Typography
                  key={item.qty}
                  className={pulse && pulse.id === item.productId ? "dt-tick" : ""}
                  style={{ fontSize: "16px", userSelect: "none", textAlign: "center" }}
                >
                  {item.qty}
                </Typography>
              </div>

              <div
                onClick={() => dispatch(increaseQty(item.productId))}
                className="dt-step"
                style={{
                  background: "#D9D9D9",
                  borderRadius: "5px",
                  width: "30px",
                  height: "30px",
                  display: "flex",
                  justifyContent: "center",
                  alignItems: "center",
                  cursor: "pointer",
                }}
              >
                <Typography style={{ fontSize: "25px", userSelect: "none" }}>+</Typography>
              </div>
            </div>
          </div>
        ))}
      </div>

      {/* FOOTER */}
      <div style={{ marginTop: "auto", display: "flex", flexDirection: "column", gap: "10px" }}>
        {/* DISCOUNT + PAYMENT SELECT — the counter's business, not a waiter's */}
        {showBilling && (
        <div style={{ display: "flex", gap: "10px", flexWrap: "wrap" }}>
          <input
            placeholder="Discount"
            type="number"
            style={{
              border: "1px solid #D4D4D4",
              padding: "12px",
              borderRadius: "10px",
              flex: 1,
              minWidth: "calc(50% - 5px)",
            }}
            value={discount === 0 ? "" : discount}
           onChange={e => dispatch(setDiscount(Number(e.target.value) || 0))} // ✅ force number
          />

          <select
            style={{
              border: "1px solid #D4D4D4",
              padding: "12px",
              borderRadius: "10px",
              flex: 1,
              minWidth: "calc(50% - 5px)",
              cursor: "pointer",
            }}
            value={paymentType}
            onChange={e => dispatch(setPaymentType(e.target.value))}
          >
            <option value="cash">Cash</option>
            {canInvoice && <option value="invoice">Invoice</option>}
          </select>
        </div>
        )}

        {/* Searchable, because a busy shop's book runs to hundreds of names
            and a plain dropdown means scrolling past all of them. */}
        {isInvoice && (
          <Select
            options={customers.map(customer => ({
              value: customer._id,
              label: customer.phone
                ? `${customer.name} · ${customer.phone}`
                : customer.name,
            }))}
            value={
              selectedCustomer
                ? {
                    value: selectedCustomer._id,
                    label: selectedCustomer.phone
                      ? `${selectedCustomer.name} · ${selectedCustomer.phone}`
                      : selectedCustomer.name,
                  }
                : null
            }
            onChange={option => dispatch(setCustomerId(option?.value))}
            placeholder={
              isCustomerDataFetched ? "Dooro Macaamiilka" : "Loading customers…"
            }
            isClearable
            noOptionsMessage={() => "No customers yet"}
            styles={{
              control: base => ({
                ...base,
                minHeight: "46px",
                borderRadius: "10px",
                borderColor: customerId ? "#D4D4D4" : "#F64829",
                boxShadow: "none",
              }),
              menu: base => ({ ...base, zIndex: 5 }),
            }}
          />
        )}

        {/* The customer's book only moves once the order is placed, so their
            standing balance is worth seeing before you add to it. */}
        {isInvoice && selectedCustomer && (
          <div
            style={{
              display: "flex",
              justifyContent: "space-between",
              fontSize: "13px",
              color: "#6B7280",
            }}
          >
            <span>{selectedCustomer.name} currently owes</span>
            <span style={{ fontWeight: "bold", color: "#C3341A" }}>
              ${Number(selectedCustomer.balance || 0).toFixed(2)}
            </span>
          </div>
        )}

        {/* RECENT ORDERS
            A waiter cannot reach the Orders screen, so this is the only way
            they can add to an order they already rang in, or hand over a
            receipt a second time. */}
        {isWaiter && (
          <RecentOrders
            orders={myOrders}
            loading={myOrdersLoading}
            editingOrderId={editingOrderId}
            onOpen={setOpenOrder}
          />
        )}

        {/* ORDER TYPE
            The black now slides from one to the next instead of blinking
            across. It is one rectangle sitting behind the three labels, moved
            to wherever the chosen one is; the chosen label simply stops
            painting its own black. Nothing here looks different once it has
            settled, and if the measurement ever fails the labels fall back to
            colouring themselves as they always did. */}
        <div
          ref={typesRef}
          style={{
            position: "relative",
            display: "flex",
            gap: "10px",
            marginTop: "10px",
            marginBottom: "20px",
          }}
        >
          {pill && (
            <span
              className="dt-pill"
              style={{
                width: pill.width + "px",
                height: pill.height + "px",
                transform: "translateX(" + pill.left + "px)",
              }}
            />
          )}

          {["dine-in", "takeaway", "delivery"].map(t => (
            <div
              key={t}
              data-order-type={orderType === t ? "active" : "idle"}
              onClick={() => dispatch(setOrderType(t))}
              style={{
                position: "relative",
                background:
                  orderType === t ? (pill ? "transparent" : "black") : "#E0E0E0",
                color: orderType === t ? "white" : "black",
                padding: "8px",
                borderRadius: "5px",
                cursor: "pointer",
                fontWeight: orderType === t ? "bold" : "normal",
                transition: "color .2s ease",
              }}
            >
              {/* The stored value is `dine-in`; only its presentation
                  changes here. */}
              {t === "dine-in" ? "Dine-in" : t.charAt(0).toUpperCase() + t.slice(1)}
            </div>
          ))}
        </div>

        {/* KITCHEN TICKET DETAILS
            Neither of these reaches the customer's receipt. The table number
            is how a runner finds where the food goes, and the note is where
            "no onions" lives — printed in the biggest type on the ticket.
            A waiter is at the table already and is not asked for either. */}
        {showBilling && (
        <div style={{ display: "flex", gap: "10px", marginBottom: "14px" }}>
          {orderType === "dine-in" && (
            <input
              value={tableNumber}
              onChange={e => dispatch(setTableNumber(e.target.value))}
              placeholder="Table"
              style={{
                width: "90px",
                flexShrink: 0,
                border: "1px solid #D4D4D4",
                borderRadius: "10px",
                padding: "11px 12px",
                fontSize: "14px",
                boxSizing: "border-box",
              }}
            />
          )}
          <input
            value={note}
            onChange={e => dispatch(setNote(e.target.value))}
            placeholder="Note for the kitchen"
            style={{
              flex: 1,
              minWidth: 0,
              border: "1px solid #D4D4D4",
              borderRadius: "10px",
              padding: "11px 12px",
              fontSize: "14px",
              boxSizing: "border-box",
            }}
          />
        </div>
        )}

        {/* TOTAL */}
        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            margin: "10px 0",
          }}
        >
          <Typography style={{ color: "#4E4E4E" }}>Total Amount</Typography>
          <Typography style={{ fontWeight: "bold" }}>
            ${shownTotal.toFixed(2)}
          </Typography>
        </div>

        {/* PLACE ORDER BUTTON */}
        <button
          className={"dt-pay" + (placed ? " dt-pay-done" : "")}
          style={{
            width: "100%",
            background: "#F64829",
            fontWeight: "bold",
            fontSize: "18px",
            color: "white",
            height: "45px",
            border: "none",
            borderRadius: "10px",
            opacity: placing ? 0.7 : 1,
            cursor: placing ? "not-allowed" : "pointer",
          }}
          onClick={placeOrder}
          disabled={placing}
        >
          {placing ? "Placing…" : placed ? "✓ Order sent" : "Place Order"}
        </button>
      </div>
    </div>
         {/* 🔹 ADDED – PRINT MODAL */}
      {printData && (
        <PrinterOrder
          open={printOpen}
          onClose={closePrint}
          business={business}
          user={user}
          data={printData}
        />
      )}

      {/* One of today's orders, with the two things a waiter may do to it. */}
      {openOrder && (
        <RecentOrderDialog
          order={openOrder}
          cartCount={cart.length}
          onClose={() => setOpenOrder(null)}
          onEdit={editRecentOrder}
          onPrint={(order) => {
            setOpenOrder(null);
            setReprintData(receiptDataFromOrder(order));
          }}
        />
      )}

      {/* A reprint. Closing it ends nothing — see reprintData above. */}
      {reprintData && (
        <PrinterOrder
          open
          onClose={() => setReprintData(null)}
          business={business}
          user={user}
          data={reprintData}
        />
      )}
      </>
  );
};

export default CartPanel;
